(() => {
  if (window.troveStudio) return;
  const desktop = matchMedia('(min-width: 750px)');
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const state = { paused: false, enabled: true, frame: 0, controllers: new Map(), animations: new Set() };
  let saved = false;
  try { saved = sessionStorage.getItem('trove-motion-paused') === 'true'; } catch (_) {}
  state.paused = saved;
  const canMove = () => state.enabled && !state.paused && !reduced.matches && !document.hidden;
  const runAnimation = (element, frames, options) => {
    if (!canMove() || !element.animate) return;
    const animation = element.animate(frames, options);
    state.animations.add(animation);
    animation.finished.catch(() => {}).finally(() => state.animations.delete(animation));
  };
  const queue = () => {
    if (state.frame) return;
    state.frame = requestAnimationFrame(() => {
      state.frame = 0;
      state.controllers.forEach(controller => controller.update());
    });
  };
  function controllerFor(section) {
    const cleanups = [];
    const hero = section.querySelector('[data-tr-hero]');
    const groups = [...section.querySelectorAll('[data-trove-video]')];
    const videos = groups.flatMap(group => [...group.querySelectorAll('video')]);
    const visibleVideos = new Set();
    const manuallyPaused = new WeakSet();
    const autoPausing = new WeakSet();
    let revealObserver;
    let videoObserver;
    const pauseVideo = video => {
      if (video.paused) return;
      autoPausing.add(video);
      video.pause();
    };
    const syncVideo = video => {
      const group = video.closest('[data-trove-video]');
      const hasSize = !!video.getClientRects().length;
      const visible = visibleVideos.has(video) && hasSize && !document.hidden;
      if (!visible) { pauseVideo(video); return; }
      if (group.dataset.autoplay !== 'true') return;
      if (!canMove()) { pauseVideo(video); return; }
      if (manuallyPaused.has(video) || !video.paused) return;
      video.muted = true;
      video.defaultMuted = true;
      video.setAttribute('playsinline', '');
      const attempt = video.play();
      if (attempt) attempt.catch(() => { video.controls = true; });
    };
    videos.forEach(video => {
      const onPause = () => {
        if (autoPausing.has(video)) autoPausing.delete(video);
        else manuallyPaused.add(video);
      };
      const onPlay = () => manuallyPaused.delete(video);
      video.addEventListener('pause', onPause);
      video.addEventListener('play', onPlay);
      cleanups.push(() => { video.removeEventListener('pause', onPause); video.removeEventListener('play', onPlay); pauseVideo(video); });
    });
    if ('IntersectionObserver' in window) {
      videoObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) visibleVideos.add(entry.target);
          else visibleVideos.delete(entry.target);
          syncVideo(entry.target);
        });
      }, { threshold: .15 });
      videos.forEach(video => videoObserver.observe(video));
      revealObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          revealObserver.unobserve(entry.target);
          runAnimation(entry.target, [{ opacity: 0, transform: 'translateY(24px)' }, { opacity: 1, transform: 'translateY(0)' }], { duration: 720, easing: 'cubic-bezier(.16,1,.3,1)', delay: Math.min(Number(entry.target.dataset.trOrder || 0) * 65, 195) });
        });
      }, { threshold: .08 });
      section.querySelectorAll('[data-tr-reveal]').forEach((element, index) => {
        element.dataset.trOrder = index % 4;
        if (element.getBoundingClientRect().top > innerHeight - 30) revealObserver.observe(element);
      });
    } else {
      videos.forEach(video => visibleVideos.add(video));
    }
    const update = () => {
      if (!hero) return;
      if (!canMove() || !desktop.matches) {
        hero.style.removeProperty('--tr-hero-y');
        hero.style.removeProperty('--tr-hero-scale');
        return;
      }
      const rect = hero.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > innerHeight) return;
      const progress = Math.min(1, Math.max(0, -rect.top / rect.height));
      hero.style.setProperty('--tr-hero-y', `${(progress * 22).toFixed(2)}px`);
      hero.style.setProperty('--tr-hero-scale', (1.055 + progress * .12).toFixed(4));
    };
    return {
      update,
      sync() { videos.forEach(syncVideo); update(); },
      destroy() { revealObserver?.disconnect(); videoObserver?.disconnect(); cleanups.forEach(fn => fn()); }
    };
  }
  function initialise(scope = document) {
    state.enabled = !document.body.classList.contains('trove-cinematic') || document.body.dataset.trMotion !== 'false';
    const sections = new Set();
    if (scope.matches?.('.shopify-section')) sections.add(scope);
    scope.querySelectorAll?.('.tr-section,.tr-guides').forEach(element => sections.add(element.closest('.shopify-section') || element));
    sections.forEach(section => {
      state.controllers.get(section)?.destroy();
      state.controllers.set(section, controllerFor(section));
    });
    scope.querySelectorAll?.('[data-tr-motion-toggle]').forEach(button => {
      button.hidden = !state.enabled || reduced.matches;
      button.setAttribute('aria-pressed', String(state.paused));
      const label = button.querySelector('[data-tr-motion-label]');
      if (label) label.textContent = state.paused ? label.dataset.resume : label.dataset.pause;
    });
    document.body.classList.toggle('tr-motion-paused', state.paused || reduced.matches || !state.enabled);
    queue();
  }
  function refresh() {
    state.animations.forEach(animation => animation.cancel());
    document.body.classList.toggle('tr-motion-paused', state.paused || reduced.matches || !state.enabled);
    document.querySelectorAll('[data-tr-motion-toggle]').forEach(button => {
      button.hidden = !state.enabled || reduced.matches;
      button.setAttribute('aria-pressed', String(state.paused));
      const label = button.querySelector('[data-tr-motion-label]');
      if (label) label.textContent = state.paused ? label.dataset.resume : label.dataset.pause;
    });
    state.controllers.forEach(controller => controller.sync());
  }
  document.addEventListener('click', event => {
    const button = event.target.closest('[data-tr-motion-toggle]');
    if (!button) return;
    state.paused = !state.paused;
    try { sessionStorage.setItem('trove-motion-paused', String(state.paused)); } catch (_) {}
    refresh();
  });
  document.addEventListener('shopify:section:load', event => initialise(event.target));
  document.addEventListener('shopify:section:unload', event => {
    state.controllers.get(event.target)?.destroy();
    state.controllers.delete(event.target);
  });
  document.addEventListener('visibilitychange', refresh);
  desktop.addEventListener('change', refresh);
  reduced.addEventListener('change', refresh);
  addEventListener('resize', queue, { passive: true });
  addEventListener('scroll', queue, { passive: true });
  window.troveStudio = { refresh };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialise(), { once: true });
  else initialise();
})();
